import { useEffect, useRef } from "react";
import { emit, listen } from "@tauri-apps/api/event";
import { invoke } from "@tauri-apps/api/core";
import { releaseChannel } from "@/lib/release-channel";
import { useUpdateSettings } from "./update-settings";
import { useNotificationStore } from "@/stores/notification-store";
import { getWindowRoleSync, useWindowRole } from "./use-window-role";
import { safeTauriUnlisten } from "./tauri-listener-cleanup";
import { track } from "./observability";
import { useDesktopUpdateStore } from "./desktop-update-store";
import {
  FOCUS_CHECK_MIN_GAP_MS,
  isPreviewChannel,
  launchJitterMs,
  periodicIntervalMs,
} from "./desktop-update-cadence";
import { desktopVersionLabel } from "@/lib/desktop-version-label";
import { translate } from "@/i18n";
import { updatesMessages } from "@/i18n/messages/updates";
import {
  MANUAL_DESKTOP_DOWNLOAD_EVENT,
  useDesktopUpdateDownload,
} from "./desktop-update-download";
import {
  shouldShowDesktopUpdateNotification,
  snoozeDesktopUpdateNotification,
} from "./desktop-update-notification-gate";
import {
  armPendingDesktopUpdateCheck,
  clearDesktopUpdateSnooze,
  scheduleDesktopUpdateCheckTomorrow,
} from "./desktop-update-snooze";
import {
  CHECKING_TOAST_ID,
  DOWNLOADING_TOAST_ID,
  showDesktopUpdateProgress,
  showLocalDevUpdateNotice,
} from "./desktop-update-feedback";

const TOAST_ID = "desktop-auto-update";
const INSTALLING_TOAST_ID = "update-installing";
const ERROR_TOAST_ID = "update-install-error";
const INSTALL_STAGED_UPDATE_EVENT = "denker://install-staged-desktop-update";
const SNOOZE_STAGED_UPDATE_EVENT = "denker://snooze-staged-desktop-update";
/** `FORGOTTEN_EVENT` in `desktop_update.rs`. */
const STAGED_UPDATE_FORGOTTEN_EVENT = "denker://desktop-update-forgotten";

/** A newer build on this channel's feed. Rust holds the update itself and
 *  its downloaded bytes between the steps (`desktop_update.rs`). */
export interface Update {
  version: string;
  body: string | null;
  /** `critical: true` in the feed marks a hotfix users shouldn't dismiss
   *  (security patch, data-corruption fix). */
  critical: boolean;
}

let stagedUpdate: Update | null = null;
let stagedVersion: string | null = null;
interface UpdateCheckRun {
  manual: boolean;
  downloadingVersion: string | null;
}
let activeCheck: UpdateCheckRun | null = null;

// Wall-clock timestamp of the most recent check() invocation. Shared
// across the launch/periodic/focus triggers so each one's debounce sees
// the others — e.g. a focus event right after launch is a no-op.
let lastCheckedAt = 0;

// Single-flight guard: concurrent triggers (periodic + focus + WS push
// landing together) join the in-flight run instead of racing it. Without
// this, two near-simultaneous calls both pass the staged-version test
// after check() resolves and download the same archive twice.
let checkPromise: Promise<void> | null = null;

function checkAndNotify(manual: boolean): Promise<void> {
  // A locally signed development build must never replace itself from a public feed.
  if (releaseChannel() === "local") {
    if (manual) showLocalDevUpdateNotice();
    return Promise.resolve();
  }
  if (checkPromise) {
    if (manual && activeCheck) {
      activeCheck.manual = true;
      showDesktopUpdateProgress(activeCheck.downloadingVersion);
    }
    return checkPromise;
  }
  const run: UpdateCheckRun = { manual, downloadingVersion: null };
  activeCheck = run;
  checkPromise = runCheckAndNotify(run).finally(() => {
    checkPromise = null;
    activeCheck = null;
  });
  return checkPromise;
}

/**
 * Starts a background update immediately when the compatibility service
 * identifies an older desktop. This intentionally ignores the user's
 * optional auto-update preference: a required update is still non-blocking,
 * but should not wait for the ordinary launch jitter.
 */
export function startRequiredDesktopUpdate(): Promise<void> {
  return checkAndNotify(true);
}

async function runCheckAndNotify(run: UpdateCheckRun) {
  lastCheckedAt = Date.now();
  track("desktop_update_check_started", { manual: run.manual });
  useDesktopUpdateStore.getState().setLastCheck({ status: "checking" });

  try {
    if (run.manual) showDesktopUpdateProgress(null);

    // Rust checks this channel's feed with a timeout, essential under the
    // single-flight guard: a check that never settles would pin
    // checkPromise forever and silently disable every future trigger.
    const update = await invoke<Update | null>("desktop_update_check");
    useNotificationStore.getState().removeSystemToast(CHECKING_TOAST_ID);
    if (!update) {
      track("desktop_update_check_completed", {
        manual: run.manual,
        available: false,
      });
      if (!stagedUpdate) useDesktopUpdateStore.getState().clearReady();
      useDesktopUpdateStore.getState().setLastCheck({ status: "up-to-date" });
      if (run.manual) {
        useNotificationStore.getState().addSystemToast({
          id: "update-up-to-date",
          appWide: true,
          title: translate(updatesMessages, "upToDate"),
          level: "info",
        });
      }
      return;
    }

    track("desktop_update_check_completed", {
      manual: run.manual,
      available: true,
      version: update.version,
    });

    useDesktopUpdateStore
      .getState()
      .setLastCheck({ status: "available", version: update.version });

    if (stagedUpdate && stagedVersion === update.version) {
      track("desktop_update_already_staged", {
        manual: run.manual,
        version: update.version,
      });
      showUpdateReadyToast(stagedUpdate, run.manual);
      return;
    }

    await prepareUpdate(update, run);
  } catch (e) {
    useDesktopUpdateStore.getState().setLastCheck({ status: "failed" });
    track("desktop_update_check_failed", {
      manual: run.manual,
      error:
        e instanceof Error ? e.message.slice(0, 200) : String(e).slice(0, 200),
    });
    useNotificationStore.getState().removeSystemToast(CHECKING_TOAST_ID);
    useNotificationStore.getState().removeSystemToast(DOWNLOADING_TOAST_ID);
    if (run.manual) {
      useNotificationStore.getState().addSystemToast({
        id: "update-check-error",
        appWide: true,
        title: translate(updatesMessages, "checkFailed"),
        description: e instanceof Error ? e.message : String(e),
        level: "error",
      });
    }
  }
}

async function prepareUpdate(update: Update, run: UpdateCheckRun) {
  const store = useNotificationStore.getState();
  run.downloadingVersion = update.version;
  track("desktop_update_download_started", {
    manual: run.manual,
    version: update.version,
  });
  if (run.manual) showDesktopUpdateProgress(update.version);

  try {
    await invoke("desktop_update_download", { version: update.version });
    stagedUpdate = update;
    stagedVersion = update.version;
    useDesktopUpdateStore.getState().setReadyVersion(update.version);
    track("desktop_update_download_completed", {
      manual: run.manual,
      version: update.version,
    });
    store.removeSystemToast(DOWNLOADING_TOAST_ID);
    showUpdateReadyToast(update, run.manual);
  } catch (e) {
    stagedUpdate = null;
    stagedVersion = null;
    useDesktopUpdateStore.getState().clearReady();
    track("desktop_update_download_failed", {
      manual: run.manual,
      version: update.version,
      error:
        e instanceof Error ? e.message.slice(0, 200) : String(e).slice(0, 200),
    });
    store.removeSystemToast(DOWNLOADING_TOAST_ID);
    if (run.manual) {
      store.addSystemToast({
        id: ERROR_TOAST_ID,
        appWide: true,
        title: translate(updatesMessages, "downloadFailed"),
        description: e instanceof Error ? e.message : String(e),
        level: "error",
        actionLabel: translate(updatesMessages, "downloadManually"),
        actionEvent: MANUAL_DESKTOP_DOWNLOAD_EVENT,
      });
    }
  }
}

function showUpdateReadyToast(update: Update, manual = false) {
  const firstNotificationToday = shouldShowDesktopUpdateNotification();
  if (!firstNotificationToday && !manual) return;

  const store = useNotificationStore.getState();
  store.removeSystemToast(ERROR_TOAST_ID);

  const critical = update.critical;
  const version = desktopVersionLabel(update.version);
  store.addSystemToast(() => ({
    id: TOAST_ID,
    appWide: true,
    title: critical
      ? translate(updatesMessages, "criticalTitle", { version })
      : translate(updatesMessages, "readyTitle", { version }),
    description:
      update.body && update.body.trim() !== "Routine update."
        ? update.body
        : critical
          ? translate(updatesMessages, "criticalBody")
          : translate(updatesMessages, "readyBody"),
    level: critical ? "warning" : "info",
    actionLabel: translate(updatesMessages, "installNow"),
    actionEvent: INSTALL_STAGED_UPDATE_EVENT,
    secondaryActionLabel: translate(updatesMessages, "tomorrow"),
    secondaryActionEvent: SNOOZE_STAGED_UPDATE_EVENT,
    persistent: true,
  }));
}

/** Install a downloaded update and restart; a failure shows its own toast. */
export async function installDesktopUpdate(update: Update) {
  const store = useNotificationStore.getState();
  clearDesktopUpdateSnooze();
  store.removeSystemToast(TOAST_ID);
  useDesktopUpdateStore.getState().setInstalling(true);
  track("desktop_update_install_started", { version: update.version });
  store.addSystemToast({
    id: INSTALLING_TOAST_ID,
    appWide: true,
    title: translate(updatesMessages, "installing"),
    level: "info",
  });
  try {
    await invoke("denker_prepare_update_models");
    await invoke("desktop_update_install", { version: update.version });
    track("desktop_update_install_completed", { version: update.version });
    await invoke("relaunch_app");
  } catch (e) {
    stagedUpdate = null;
    stagedVersion = null;
    useDesktopUpdateStore.getState().clearReady();
    track("desktop_update_install_failed", {
      version: update.version,
      error:
        e instanceof Error ? e.message.slice(0, 200) : String(e).slice(0, 200),
    });
    store.removeSystemToast(INSTALLING_TOAST_ID);
    const description = e instanceof Error ? e.message : String(e);
    store.addSystemToast(() => ({
      id: ERROR_TOAST_ID,
      appWide: true,
      title: translate(updatesMessages, "installFailed"),
      description,
      level: "error",
      actionLabel: translate(updatesMessages, "downloadManually"),
      actionEvent: MANUAL_DESKTOP_DOWNLOAD_EVENT,
      persistent: true,
    }));
  }
}

export async function installStagedDesktopUpdate(): Promise<boolean> {
  if (getWindowRoleSync() !== "owner") {
    try {
      await emit(INSTALL_STAGED_UPDATE_EVENT);
      return true;
    } catch (e) {
      useNotificationStore.getState().addSystemToast({
        id: "desktop-update-install-request-failed",
        appWide: true,
        title: translate(updatesMessages, "startFailed"),
        description: e instanceof Error ? e.message : String(e),
        level: "error",
      });
      return false;
    }
  }

  if (!stagedUpdate) {
    useNotificationStore.getState().addSystemToast({
      id: "desktop-update-not-ready",
      appWide: true,
      title: translate(updatesMessages, "notReady"),
      description: translate(updatesMessages, "notReadyBody"),
      level: "info",
    });
    useDesktopUpdateStore.getState().clearReady();
    return false;
  }
  await installDesktopUpdate(stagedUpdate);
  return true;
}

export function useAutoUpdate() {
  const autoUpdate = useUpdateSettings((s) => s.autoUpdate);
  const role = useWindowRole();
  useDesktopUpdateDownload(role);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-check on launch with jitter — owner window only.
  // Reactive on `role` so a window promoted to owner mid-session re-arms.
  useEffect(() => {
    if (!autoUpdate) return;
    if (role !== "owner") return;

    timerRef.current = setTimeout(() => {
      void checkAndNotify(false);
    }, launchJitterMs());

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [autoUpdate, role]);

  // Periodic re-check with per-tick jitter via chained setTimeout
  useEffect(() => {
    if (!autoUpdate) return;
    if (role !== "owner") return;

    let handle: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    function scheduleNext() {
      if (cancelled) return;
      handle = setTimeout(() => {
        void checkAndNotify(false).then(() => {
          scheduleNext();
        });
      }, periodicIntervalMs());
    }

    scheduleNext();

    return () => {
      cancelled = true;
      if (handle) clearTimeout(handle);
    };
  }, [autoUpdate, role]);

  // Recheck when the window regains focus — the cheap "I came back from
  // lunch" shortcut. Debounced by FOCUS_CHECK_MIN_GAP_MS against the last
  // check from any trigger so rapid focus/blur cycles don't hammer the
  // manifest server.
  useEffect(() => {
    if (!autoUpdate) return;
    if (role !== "owner") return;

    const onFocus = () => {
      if (Date.now() - lastCheckedAt < FOCUS_CHECK_MIN_GAP_MS) return;
      void checkAndNotify(false);
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [autoUpdate, role]);

  // Listen for manual "Check for Updates" from menu — owner only
  useEffect(() => {
    if (role !== "owner") return;

    let unlisten: (() => void) | null = null;
    let cancelled = false;
    void listen("check-for-update", () => {
      void checkAndNotify(true);
    }).then((u) => {
      if (cancelled) safeTauriUnlisten(u);
      else {
        unlisten = u;
        armPendingDesktopUpdateCheck();
      }
    });
    return () => {
      cancelled = true;
      if (unlisten) safeTauriUnlisten(unlisten);
    };
  }, [role]);

  // A matching server push means latest.json is live. Check immediately;
  // the focus debounce could hide a fresh build during preview's 5-minute cadence.
  useEffect(() => {
    if (!autoUpdate) return;
    if (role !== "owner") return;

    let unlisten: (() => void) | null = null;
    let cancelled = false;
    void listen<{ channel?: string; version?: string }>(
      "denker://desktop-update-available",
      (event) => {
        const broadcastChannel = event.payload?.channel ?? "";
        const ourChannel = isPreviewChannel() ? "preview" : "prod";
        if (broadcastChannel !== ourChannel) return;
        void checkAndNotify(false);
      },
    ).then((u) => {
      if (cancelled) safeTauriUnlisten(u);
      else unlisten = u;
    });
    return () => {
      cancelled = true;
      if (unlisten) safeTauriUnlisten(unlisten);
    };
  }, [autoUpdate, role]);

  // Mirrors can display the compact update button, but only the owner
  // webview holds the staged updater object. Route mirror clicks here.
  useEffect(() => {
    if (role !== "owner") return;

    let unlisten: (() => void) | null = null;
    let cancelled = false;
    void listen(INSTALL_STAGED_UPDATE_EVENT, () => {
      void installStagedDesktopUpdate();
    }).then((u) => {
      if (cancelled) safeTauriUnlisten(u);
      else unlisten = u;
    });
    return () => {
      cancelled = true;
      if (unlisten) safeTauriUnlisten(unlisten);
    };
  }, [role]);

  // "Tomorrow" is a serializable event-backed action so it works from
  // mirror webviews where JavaScript callback functions cannot be copied.
  useEffect(() => {
    if (role !== "owner") return;

    let unlisten: (() => void) | null = null;
    let cancelled = false;
    void listen(SNOOZE_STAGED_UPDATE_EVENT, () => {
      snoozeDesktopUpdateNotification();
      scheduleDesktopUpdateCheckTomorrow();
      useNotificationStore.getState().removeSystemToast(TOAST_ID);
      track("desktop_update_snoozed", { version: stagedVersion });
    }).then((u) => {
      if (cancelled) safeTauriUnlisten(u);
      else unlisten = u;
    });
    return () => {
      cancelled = true;
      if (unlisten) safeTauriUnlisten(unlisten);
    };
  }, [role]);

  // Rust dropped the downloaded update when the Preview pick changed; drop it here too.
  useEffect(() => {
    if (role !== "owner") return;

    let unlisten: (() => void) | null = null;
    let cancelled = false;
    void listen(STAGED_UPDATE_FORGOTTEN_EVENT, () => {
      stagedUpdate = null;
      stagedVersion = null;
      useDesktopUpdateStore.getState().clearReady();
      useNotificationStore.getState().removeSystemToast(TOAST_ID);
    }).then((u) => {
      if (cancelled) safeTauriUnlisten(u);
      else unlisten = u;
    });
    return () => {
      cancelled = true;
      if (unlisten) safeTauriUnlisten(unlisten);
    };
  }, [role]);
}
